/* import __COLOCATED_TEMPLATE__ from './history.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { htmlSafe } from '@ember/template';
import { copyLinkToClipboard } from 'sh-www/utils/url';
import { ACTION } from 'sh-www/models/action';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-admin-blaster-history';
const FILTER_CLASS_NAME = `${CLASS_NAME}__filter`;
const FILTER_ACTIVE_CLASS_NAME = `${FILTER_CLASS_NAME}--active`;

const STATUSES = {
  ALL: 'all',
  APPLIED: 'applied',
  OPTED_OUT: 'optedOut',
  DECLINED: 'declined',
  OUTSTANDING: 'outstanding',
  HIRED: 'hired',
};

const FILTER_TITLES = {
  [STATUSES.ALL]: 'All Notified',
  [STATUSES.APPLIED]: 'Applied',
  [STATUSES.OPTED_OUT]: 'Opted out',
  [STATUSES.DECLINED]: 'Declined',
  [STATUSES.OUTSTANDING]: 'Outstanding',
  [STATUSES.HIRED]: 'Hired',
};

const BLAST_HISTORY_TABLE_BASE_COLUMNS = {
  NAME: 'Name',
  EMAIL: 'Email',
  RATING: 'Rating',
  LOCATION: 'Location',
  LAST_LOCATION: 'Last Location',
  LAST_SEEN: 'Last Seen',
  NOTIFIED_COUNT: 'Notified Count',
};

const BLAST_HISTORY_TABLE_ADDITIONAL_COLUMNS = {
  [STATUSES.ALL]: {
    STATUS: 'Status',
  },
  [STATUSES.APPLIED]: {
    MESSAGE_RECEIVED: 'Message From Buyer',
  },
  [STATUSES.OPTED_OUT]: {
    REASON: 'Reason',
    FEEDBACK: 'Feedback',
  },
  [STATUSES.HIRED]: {
    RATE: 'Rate',
    HIRE_DATE: 'Date of Hire',
  },
};

const NULL_BLAST_FILTER_OPTION_LABEL = 'All Blasts';
const BLAST_FILTER_LABEL_DATE_FORMAT = 'YYYY-MM-DD kk:mm';

export const ADMIN_BLASTER_HISTORY_COMPONENT = {
  STATUSES,
  FILTER_TITLES,
};

export default class AdminBlasterHistory extends Component {
  @service('state-managers/application') applicationStateManager;
  @service moment;

  @tracked filterByBlast = null;

  get classNames() {
    return clsx(
      CLASS_NAME,
      this.args.class,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get filterClassName() {
    return FILTER_CLASS_NAME;
  }

  get filterClassNamesActive() {
    return clsx(
      FILTER_CLASS_NAME,
      FILTER_ACTIVE_CLASS_NAME,
    );
  }

  get tableStyles() {
    return htmlSafe(`--history-table-cols: ${this.columnCount};`);
  }

  get filterTitles() {
    return FILTER_TITLES;
  }

  get activeStatusFilter() {
    return this.args.status ?? STATUSES.ALL;
  }

  get baseColumns() {
    return BLAST_HISTORY_TABLE_BASE_COLUMNS;
  }

  get additionalColumns() {
    return BLAST_HISTORY_TABLE_ADDITIONAL_COLUMNS?.[this.activeStatusFilter] ?? {};
  }

  get columnCount() {
    return Object.keys(BLAST_HISTORY_TABLE_BASE_COLUMNS).length + Object.keys(this.additionalColumns).length;
  }

  get blastFilterValue() {
    return this.blastFilterOptions.find((option) => option.model.id === this.filterByBlast)?.key;
  }

  get blastFilterOptions() {
    return [
      {
        key: NULL_BLAST_FILTER_OPTION_LABEL,
        model: { id: null, label: NULL_BLAST_FILTER_OPTION_LABEL },
      },
      ...Object.values(this.args.blastHistory).map((blast) => ({
        key: `${this.moment.moment(blast.createdAt).format(BLAST_FILTER_LABEL_DATE_FORMAT)} (${blast.blastCount})`,
        model: { 
          id: blast.id,
          label: `${this.moment.moment(blast.createdAt).format(BLAST_FILTER_LABEL_DATE_FORMAT)} (${blast.blastCount})`,
        },
      })),
    ];
  }

  /**
   * A list of users that are blasted under the current blast filter.
   * 
   * @returns {Array<User>}
   */
  get blastedUsers() {
    if (this.filterByBlast) {
      const { blastUsers } = this.#blastIdToBlastHistoryMap[this.filterByBlast];
      return blastUsers.reduce((accum, blastUser) => {
        if (blastUser.userWasBlasted) {
          accum.push(blastUser.user);
        }

        return accum;
      }, []);
    }

    return this.args.blastedUsers;
  }

  #userIdToBlastedUserMap = createCache(() => {
    return this.blastedUsers.reduce((accum, blastedUser) => {
      accum[blastedUser.id] = blastedUser;
      return accum;
    }, {});
  });

  /**
   * A unique mapping of user id to the blasted user.
   */
  get userIdToBlastedUserMap() {
    return getValue(this.#userIdToBlastedUserMap);
  }

  #categorizedBlastedUsers = createCache(() => {
    const { userIdToBlastedUserMap } = this;

    const applicationIdToBlastedUserMap = this.#applicationIdToBlastedUserMap;
    const categoryEnumToApplicationIds = this.#categoryEnumToApplicationIds;

    // collect the user models for each category
    const applied = categoryEnumToApplicationIds.applied?.reduce((accum, appId) => {
      const blastedSeller = applicationIdToBlastedUserMap[appId];
      if (blastedSeller) {
        accum.push(blastedSeller);
      }

      return accum;
    }, []) ?? [];

    const withdrawn = categoryEnumToApplicationIds.optedOut?.reduce((accum, appId) => {
      const blastedSeller = applicationIdToBlastedUserMap[appId];
      if (blastedSeller) {
        accum.push(blastedSeller);
      }

      return accum;
    }, []) ?? [];
    const optedOut = Object.values(this.blastedUserIdToAssignmentUserMetaMap).reduce((accum, { user }) => {
      accum.push(user);
      return accum;
    }, withdrawn);

    const declined = categoryEnumToApplicationIds.declined?.reduce((accum, appId) => {
      const blastedSeller = applicationIdToBlastedUserMap[appId];
      if (blastedSeller) {
        accum.push(blastedSeller);
      }

      return accum;
    }, []) ?? [];

    const hired = categoryEnumToApplicationIds.hired?.reduce((accum, appId) => {
      const blastedSeller = applicationIdToBlastedUserMap[appId];
      if (blastedSeller) {
        accum.push(blastedSeller);
      }

      return accum;
    }, []) ?? [];

    const categorizedUserIds = [
      ...applied,
      ...optedOut,
      ...declined,
      ...hired,
    ].reduce((accum, { id }) => {
      accum.push(id);
      return accum;
    }, []);

    // outstanding is the array diff between blast users and categorized users
    const outstanding = Object.values(userIdToBlastedUserMap).filter(({ id }) => !categorizedUserIds.includes(id));

    return {
      [STATUSES.ALL]: Object.values(userIdToBlastedUserMap).sort((a, b) => (a.fullName > b.fullName) ? 1 : -1),
      [STATUSES.APPLIED]: applied.sort((a, b) => (a.fullName > b.fullName) ? 1 : -1),
      [STATUSES.OPTED_OUT]: optedOut.sort((a, b) => (a.fullName > b.fullName) ? 1 : -1),
      [STATUSES.DECLINED]: declined.sort((a, b) => (a.fullName > b.fullName) ? 1 : -1),
      [STATUSES.OUTSTANDING]: outstanding.sort((a, b) => (a.fullName > b.fullName) ? 1 : -1),
      [STATUSES.HIRED]: hired.sort((a, b) => (a.fullName > b.fullName) ? 1 : -1),
    }
  });

  get categorizedBlastedUsers() {
    return getValue(this.#categorizedBlastedUsers);
  }

  /**
   * A map from a user id that was blasted to their most recent application.
   * Not all blast users make an application.
   */
  get blastedSellerIdToApplicationMap() {
    return this.args.blastedUsersApplications.reduce((accum, application) => {
      accum[application.seller.id] = application;

      return accum;
    }, {});
  }

  /**
   * Get which category an application falls under. Not all blast users make an application.
   * Keyed by application id.
   */
  get blastedUserApplicationCategory() {
    const applicationIdToWithdrawnApplication = this.#applicationIdToWithdrawnApplication;

    return Object.values(this.blastedSellerIdToApplicationMap).reduce((accum, application) => {
      const optedOut = applicationIdToWithdrawnApplication[application.id];
      if (application.status >= 3) {
        accum[application.id] = STATUSES.HIRED;
      } else if (application.status >= 0 && application.status < 3) {
        accum[application.id] = STATUSES.APPLIED;
      } else if (application.status === "-1" && optedOut) {
        accum[application.id] = STATUSES.OPTED_OUT;
      } else {
        accum[application.id] = STATUSES.DECLINED;
      }
      
      return accum;
    }, {});
  }

  /**
   * Get the counts for each category an application falls under.
   * Keyed by status enum.
   */
   get blastedUserApplicationCategoryCounts() {
    const { categorizedBlastedUsers } = this;
    return Object.values(STATUSES).reduce((accum, category) => {
      accum[category] = categorizedBlastedUsers[category].length;
      return accum;
    }, {});
  }

  /**
   * Create a map keyed by a blasted user id to their assignment user meta.
   */
  get blastedUserIdToAssignmentUserMetaMap() {
    const userIdToAssignmentUserMetaMap = this.#userIdToAssignmentUserMetaMap;
    return Object.keys(this.userIdToBlastedUserMap).reduce((accum, userId) => {
      const assignmentUserMeta = userIdToAssignmentUserMetaMap[userId];
      if (assignmentUserMeta) {
        accum[userId] = assignmentUserMeta;
      }

      return accum;
    }, {})
  }

  /**
   * Create a map keyed by user id to the number of posting invites
   */
   get userIdToPostingInviteCountMap() {
    // use blastedUsers argument since blast filter is irrelevant
    return this.args.blastedUsers.reduce((accum, { id }) => {
      accum[id] = (accum[id] || 0) + 1;

      return accum;
    }, {});
  }

  get #blastIdToBlastHistoryMap() {
    return this.args.blastHistory.reduce((accum, historyRecord) => {
      accum[historyRecord.id] = historyRecord;
      return accum;
    }, {});
  }

  /**
   * Create a map keyed by user id to their assignment user meta.
   * This contains users that may not have been blasted.
   */
  get #userIdToAssignmentUserMetaMap() {
    const { assignmentUserMetas } = this.args.posting;

    return assignmentUserMetas
      .reduce((accum, assignmentUserMeta) => {
        if (assignmentUserMeta.notInterested) {
          accum[assignmentUserMeta.user.id] = assignmentUserMeta;
        }

        return accum;
      }, {});
  }

  /**
   * Create a map keyed by the most recent application id to the blasted seller
   */
  get #applicationIdToBlastedUserMap() {
    const { blastedSellerIdToApplicationMap } = this;
    const { userIdToBlastedUserMap } = this;

    return Object.keys(blastedSellerIdToApplicationMap).reduce((accum, userId) => {
      const application = blastedSellerIdToApplicationMap[userId];
      accum[application.id] = userIdToBlastedUserMap[application.seller.id];

      return accum;
    }, {});
  }

  /**
   * Create a map keyed by {STATUSES.*} to the application ids with that status
   */
  get #categoryEnumToApplicationIds() {
    const { blastedUserApplicationCategory } = this;

    return Object.keys(blastedUserApplicationCategory).reduce((accum, applicationId) => {
      const category = blastedUserApplicationCategory[applicationId];
      if (!accum[category]) {
        accum[category] = [];
      }
      accum[category].push(applicationId);
      return accum;
    }, {});
  }

  get #applicationIdToActions() {
    return this.args.blastedUsersApplications.reduce((accum, { actions }) => {
      accum = accum.concat(actions.toArray());
      return accum;
    }, []);
  }

  get #applicationIdToWithdrawnApplication() {
    const applicationIdToActions = this.#applicationIdToActions;

    return applicationIdToActions.reduce((accum, action) => {
      if (action.name === ACTION.NAMES.PROJECT_WITHDRAW) {
        accum[action.actionable.id] = action.actionable;
      }

      return accum;
    }, {});
  }

  @action
  setBlastFilter(model) {
    this.filterByBlast = model.id;
  }

  @action
  onEmailClicked(email) {
    copyLinkToClipboard(email);
    this.applicationStateManager.addToast('Email copied');
  }
}
